import { motion } from 'framer-motion'
import { Calendar, Compass, HeartHandshake } from 'lucide-react'

const pillars = [
  {
    icon: HeartHandshake,
    title: 'A real trainer behind every plan',
    body:
      'Your workouts and nutrition come from a coach who knows your goals — not a generic algorithm. Boomshift just makes their work part of your daily life, instead of a PDF they emailed you once.',
  },
  {
    icon: Calendar,
    title: 'Built for real life',
    body:
      "Workouts that scale to home, hotel, or a real gym. Skipped a day? You'll log why — and your trainer sees it. No shame, no broken streaks.",
  },
  {
    icon: Compass,
    title: 'Track only what matters to you',
    body:
      'Strength, weight, photos, how you feel. Snap a picture for food logging instead of counting calories. Skip the rest.',
  },
]

export function Pillars() {
  const transitionBase = {
    duration: 0.6,
    ease: 'easeOut' as const,
  }

  return (
    <section className="relative bg-charcoal-black px-6 py-20 md:py-28">
      <header className="mx-auto max-w-2xl text-center">
        <p className="inline-flex items-center gap-2 rounded-full border border-warm-amber/25 bg-warm-amber/[0.06] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-warm-amber/90">
          What you get
        </p>
        <h2 className="mt-4 text-[2.1rem] font-semibold leading-[1.1] tracking-tight text-soft-white md:text-5xl">
          Three things, done right.
        </h2>
        <p className="mx-auto mt-4 max-w-prose text-lg leading-relaxed text-muted-silver">
          No 47 features you&apos;ll never use. Just the parts that actually move the needle.
        </p>
      </header>

      <div className="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {pillars.map((pillar, index) => {
          const Icon = pillar.icon
          return (
            <motion.div
              key={pillar.title}
              className="relative rounded-2xl border border-white/[0.06] bg-soft-black/60 p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-white/15 md:p-8"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ ...transitionBase, delay: index * 0.08 }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-warm-amber/20 bg-warm-amber/10">
                <Icon className="text-warm-amber" size={24} aria-hidden />
              </div>
              <h3 className="mt-5 text-xl font-semibold leading-snug text-soft-white">{pillar.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-silver md:text-base">{pillar.body}</p>
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}
